import { useState, useRef } from 'react';
import classNames from 'classnames';
import { useMutation } from '@apollo/client';
import { message } from 'antd';
import { getSegmentFromLocale } from '$util/getSegment';
import { Card } from '$components/basic/Card';
import { ForgotPasswordForm } from '$components/composite/ForgotPassword/components/ForgotPasswordForm';
import { useAppContext } from '$util/AppContext';
import type { IFormContext } from '$components/composite/Form';
import { getShortTextByKey, renderRichTextByKey } from '$cms/getDataByKey';
import type { CmsContentDictionary } from '$cms/index';
import { changeUrl } from '$util/index';
import { Title } from '@/components/atoms/Title';
import { Button, DesignType } from '@/components/atoms/Button';
import { Styled } from './styled';
import { MUTATION_RESET_PASSWORD } from './gql';

const backToLogin = () => {
  changeUrl({
    pathname: '/login',
  });
};

const getCmsData = (cmsData: CmsContentDictionary) => {
  const getShortText = getShortTextByKey.bind(cmsData);
  const getRichText = renderRichTextByKey.bind(cmsData);

  return {
    title: getShortText('forgotPassword.title', 'Forgot your password?'),
    sentTitle: getShortText('forgotPassword.sentTitle', 'Email sent'),
    instructions: getShortText(
      'forgotPassword.instructions',
      "Enter your email address and we'll send you a link to reset your password."
    ),
    submit: getShortText('forgotPassword.submitButton', 'Send email'),
    submittedMessage: getRichText(
      'forgotPassword.submittedMessage',
      <>
        <p>
          If the email address you supplied is in our system, you&rsquo;ll receive an email with instructions
          on how to reset your password.
        </p>
        <p>
          If you continue to have problems, or don&rsquo;t receive an email, please contact us and we&rsquo;ll
          help recover your account.
        </p>
      </>
    ),
    login: getShortText('forgotPassword.login', 'Login'),
    label: getShortText('forgotPassword.label', 'Email address'),
    placeholder: getShortText('forgotPassword.placeholder', 'Enter your email'),
    errorMessage: getShortText(
      'forgotPassword.errorMessage',
      'Something went wrong while trying to reset your password, please try again or contact us if the issue persists.'
    ),
  };
};

export interface ForgotPasswordContainerProps {
  readonly className?: string;
}

export function ForgotPasswordContainer({ className }: ForgotPasswordContainerProps): JSX.Element {
  const AppContext = useAppContext();
  const {
    cmsContent: { dictionary },
    locale,
  } = AppContext;

  const {
    title,
    sentTitle,
    instructions,
    submit,
    submittedMessage,
    login,
    label,
    placeholder,
    errorMessage,
  } = getCmsData(dictionary);

  const [emailSent, setEmailSent] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [resetPassword] = useMutation(MUTATION_RESET_PASSWORD, {
    onError: () => {
      setIsBusy(false);
      return message.error(errorMessage);
    },
  });
  const formContextRef = useRef<IFormContext>();

  const forgotPassword = async ({ email }: { [key: string]: string }) => {
    setIsBusy(true);

    const response = await resetPassword({
      variables: {
        resetPasswordInputs: {
          email,
          segment: getSegmentFromLocale(locale),
        },
      },
    });

    if (!response.errors) {
      setEmailSent(true);
    }
  };

  const submitForm = () => formContextRef?.current?.submitForm();

  return (
    <Styled className={classNames('ForgotPasswordContainer', className)}>
      <Title>{emailSent ? sentTitle : title}</Title>
      <Card className={className}>
        {emailSent ? (
          <div className="ForgotPasswordEmailSent">
            {submittedMessage}
            <Button designType={DesignType.PRIMARY} onClick={backToLogin} onKeyPress={backToLogin}>
              {login}
            </Button>
          </div>
        ) : (
          <>
            <ForgotPasswordForm
              values={{}}
              onSubmit={forgotPassword}
              onBackStep={backToLogin}
              label={label}
              placeholder={placeholder}
              backStepLabel={login}
              formContext={formContextRef}
              instructions={instructions}
            />
            <Button
              style={{
                display: 'block',
                margin: '24px auto 0',
              }}
              designType={DesignType.PRIMARY}
              size="L"
              onClick={submitForm}
              onKeyPress={submitForm}
              loading={isBusy}
            >
              {submit}
            </Button>
          </>
        )}
      </Card>
    </Styled>
  );
}
